/**
 * Builder for step list elements
 */
import { StepListElementBuilderInterface } from '../element-interfaces';
import { BaseBuilder } from './BaseBuilder';
import { BlockElement } from '../../BlockRenderer/types';
import { deepClone } from '../utils';

/**
 * Builder for creating step list elements
 * 
 * @example
 * ```typescript
 * const stepList = Elements.StepList()
 *   .addStep(Elements.Step().addElement(Elements.Text().text('Step 1 content').build()).build())
 *   .addStep(Elements.Step().addElement(Elements.Text().text('Step 2 content').build()).build())
 *   .build();
 * ```
 */
export class StepListElementBuilder extends BaseBuilder<BlockElement, StepListElementBuilderInterface> implements StepListElementBuilderInterface {
  /**
   * Creates a new StepListElementBuilder instance
   */
  constructor() {
    super({
      type: 'step_list',
      elements: []
    });
  }

  /**
   * Creates a new StepListElementBuilder instance
   */
  static builder(): StepListElementBuilderInterface {
    return new StepListElementBuilder();
  }

  /**
   * Adds a step to the step list
   * 
   * @param step - Step to add to the step list
   */
  addStep(step: BlockElement | { build(): BlockElement }): StepListElementBuilderInterface {
    const copy = this.copy();
    const stepJson = 'build' in step ? step.build() : deepClone(step);
    
    // Ensure the step has type 'step' to match StepBlock interface
    if (stepJson.type !== 'step') {
      throw new Error('Steps added to StepList must have type "step"');
    }
    
    copy.data.elements = [...(copy.data.elements as BlockElement[] || []), stepJson];
    return copy;
  }

  /**
   * Adds multiple steps at once to the step list
   * 
   * @param steps - Steps to add to the step list
   */
  steps(...steps: (BlockElement | { build(): BlockElement })[]): StepListElementBuilderInterface {
    let result = this.copy();
    for (const step of steps) {
      result = result.addStep(step);
    }
    return result;
  }

  /**
   * Builds and returns the final step list element
   */
  build(): BlockElement {
    if (!this.data.elements || (this.data.elements as BlockElement[]).length === 0) {
      throw new Error('Step list must have at least one step');
    }

    // Cast elements to StepBlock[] since we've validated each step has type 'step'
    // in the addStep method
    return {
      type: 'step_list',
      elements: this.data.elements as { type: 'step'; elements: BlockElement[] }[]
    };
  }
}
